import React from 'react';
import {Plugins, Atom} from '@packages/systems/wfdl';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {hasValue} from '@packages/utilities/string-utils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import withAttribute from './withAttribute';
import {
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_ADD_TO_CART_PILL,
} from '@packages/systems/commerce/constants';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';

import {TListOfElement} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {EvaluatedNodeData, getChildren} from '@packages/utilities/data-utils';

const CommerceAddToCartOptionPill = Atom.createRender(
  ({
    attributes,
    classes,
    generateAttributes,
    context,
    data,
    environmentContext,
    renderChild,
  }) => {
    const runtimeEnvironment = useRuntimeEnvironment();
    const option = context.get('data', {id: ''});
    const value = option.id;
    const customNodeState = environmentContext.get('customNodeState');
    const newClasses =
      runtimeEnvironment === WebflowEnvironments.DESIGNER &&
      customNodeState &&
      (customNodeState.includes('ecommerce-pill-selected') ||
        customNodeState.includes('ecommerce-pill-disabled'))
        ? classes.push(`w--${customNodeState}`)
        : classes;
    return (
      <div
        {...generateAttributes(attributes, newClasses)}
        role="radio"
        aria-checked="false"
        aria-disabled="false"
        tabIndex={-1}
        data-option-id={value && hasValue(value) ? value : ''}
      >
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, context)
        )}
      </div>
    );
  }
);

const config = createIntegratedAtom({
  displayName: 'Button',
  showEmpty: false,
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Button')),
  },
  fields: {children: TListOfElement},
  constraints: {
    ancestors: [{is: 'CommerceAddToCartOptionPillGroup', rule: 'ExactlyOne'}],
  },
  style: {
    base: {
      // This FlowFixMe is here because ecommerce-pill-selected and disabled aren't technically pseudos, but custom node states
      // These states aren't part of our Flow types, and when I tried to add them, it created ~400 Flow errors, so... here we are!

      main: {
        noPseudo: {
          marginRight: px(10),
          paddingTop: px(8),
          paddingBottom: px(8),
          paddingLeft: px(15),
          paddingRight: px(15),
          borderTopWidth: px(1),
          borderBottomWidth: px(1),
          borderLeftWidth: px(1),
          borderRightWidth: px(1),
          borderTopColor: createRgbaColor('black'),
          borderBottomColor: createRgbaColor('black'),
          borderLeftColor: createRgbaColor('black'),
          borderRightColor: createRgbaColor('black'),
          borderTopStyle: 'solid',
          borderBottomStyle: 'solid',
          borderLeftStyle: 'solid',
          borderRightStyle: 'solid',
          color: createRgbaColor('black'),
          backgroundColor: createRgbaColor('white'),
          cursor: 'pointer',
        },
        // @ts-expect-error - TS2322 - Type '{ noPseudo: { marginRight: string; paddingTop: string; paddingBottom: string; paddingLeft: string; paddingRight: string; borderTopWidth: string; borderBottomWidth: string; borderLeftWidth: string; ... 11 more ...; cursor: "pointer"; }; 'ecommerce-pill-selected': { ...; }; 'ecommerce-pill-disabled': { ...; }; }' is not assignable to type 'Partial<Record<"lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within", WebflowCssProperties>>'.
        'ecommerce-pill-selected': {
          color: createRgbaColor('white'),
          backgroundColor: createRgbaColor('black'),
        },
        'ecommerce-pill-disabled': {
          color: createRgbaColor('#666666'),
          backgroundColor: createRgbaColor('#e6e6e6'),
          borderTopColor: createRgbaColor('#e6e6e6'),
          borderBottomColor: createRgbaColor('#e6e6e6'),
          borderLeftColor: createRgbaColor('#e6e6e6'),
          borderRightColor: createRgbaColor('#e6e6e6'),
          cursor: 'not-allowed',
          outlineStyle: 'none',
        },
      },
    },
  },
  render: withAttribute(
    DATA_ATTR_NODE_TYPE,
    NODE_TYPE_COMMERCE_ADD_TO_CART_PILL
  )(CommerceAddToCartOptionPill),
});

export default config;
